// Shared TypeScript shapes for the dashboard ↔ API contract. Loose by design —
// the API may add fields; we only depend on what we render.

export interface Me {
  id: number;
  email: string;
  name?: string;
  keycloakSub: string;
  /** IANA timezone the dashboard buckets analytics in. Default 'UTC'. */
  timezone: string;
}

export type ApiKeyKind = 'publishable' | 'secret';

export interface ApiKey {
  id: number;
  kind: ApiKeyKind;
  prefix: string;
  lastFour: string;
  createdAt: string;
  revokedAt?: string | null;
}

export interface RotatedKey extends ApiKey {
  // Only returned on POST /v1/keys/rotate — full plaintext shown once.
  token: string;
}

export interface AppRecord {
  id: number;
  slug: string;
  name: string;
  description?: string | null;
  androidPackage?: string | null;
  iosBundleId?: string | null;
  androidUrl?: string | null;
  iosUrl?: string | null;
  webUrl?: string | null;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AppInput {
  name: string;
  androidPackage?: string;
  iosBundleId?: string;
  androidUrl?: string;
  iosUrl?: string;
  webUrl?: string;
  description?: string;
}

export type CampaignType = 'redirect' | 'landing';
export type CampaignStatus = 'draft' | 'active' | 'paused' | 'archived';
export type CampaignSource =
  | 'facebook'
  | 'instagram'
  | 'telegram'
  | 'email'
  | 'sms'
  | 'print'
  | 'influencer'
  | 'website_banner'
  | 'other';

// Single source of truth for campaign sources. UI displays via `label`;
// the wire format and DB column store `value`. Order here drives the form
// dropdown order. When you add a new source, update the backend CHECK
// constraint (impl §1.4) and the icon switch in `lib/breakdownIcons.tsx`.
export const SOURCES: { value: CampaignSource; label: string }[] = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'print', label: 'Print / QR' },
  { value: 'influencer', label: 'Influencer' },
  { value: 'website_banner', label: 'Website banner' },
  { value: 'other', label: 'Other' },
];

export interface Campaign {
  id: number;
  code: string;
  name: string;
  appId: number;
  type: CampaignType;
  source: CampaignSource;
  deepLink?: string | null;
  expiresAt?: string | null;
  status: CampaignStatus;
  notes?: string | null;
  createdAt: string;
  // Optional roll-up fields the API may include on list.
  clicks?: number;
  installs?: number;
  signups?: number;
}

export interface CampaignInput {
  name: string;
  appId: number;
  type: CampaignType;
  source: CampaignSource;
  deepLink?: string;
  expiresAt?: string;
  notes?: string;
}

// ============================================================
// Analytics / stats shapes — must match the controllers under
// /v1/campaigns/{id}/stats/* and /v1/stats/*.
// ============================================================

export type EventType =
  | 'click'
  | 'landing_view'
  | 'landing_click'
  | 'outbound_click'
  | 'install'
  | 'signup'
  | 'app_open';

export type BreakdownDim =
  | 'platform'
  | 'target_platform'
  | 'country'
  | 'os'
  | 'browser'
  | 'device_class'
  | 'webview_app'
  | 'source';

export interface DateRange {
  from: string; // ISO 8601 instant
  to: string;
}

export interface TimelinePoint {
  day: string; // YYYY-MM-DD (UTC)
  clicks: number;
  landingClicks: number;
  installs: number;
  signups: number;
}

export interface BreakdownItem {
  key: string;
  count: number;
}

export interface HeatmapCell {
  dow: number; // 0 = Sun … 6 = Sat
  hour: number; // 0 … 23 (UTC)
  count: number;
}

export interface StatsTotals {
  clicks: number;
  landingViews: number;
  landingClicks: number;
  installs: number;
  signups: number;
  appOpens: number;
  bots: number;
}

export interface TopCampaign {
  code: string;
  name: string | null;
  source: string | null;
  clicks: number;
  installs: number;
  signups: number;
}

export interface OverviewResponse {
  from: string;
  to: string;
  totals: StatsTotals;
  topCampaigns: TopCampaign[];
}
